/**
 * PURE. The creatures §A12 DNA codec: 56 raw bytes (448 bits, MSB-first, CRC-10 over bits 0–437) ⇄ `CreatureDNA` ⇄ the
 * share string `"M" + 90 Crockford base32 chars [+ "." + base64url(custom name)]`.
 */
import { DNA_BYTES, DNA_CODE_LENGTH, type CreatureDNA, type DnaCodec } from '../../shared';
import { base64urlDecode, base64urlEncode, crc10, fromBase32, readBits, toBase32, writeBits } from './bits';
import { DNA_BITS, DNA_FIELDS, fieldMax, FIELD_OFFSET } from './fields';
import { generatedName, validateCustomName } from './names';
import { dnaFromFields, fieldsOf, type FieldMap } from './presets';

const CRC_BITS = 438;
const PAYLOAD_CHARS = DNA_CODE_LENGTH - 1; // 90

/** Field map → 56 bytes with a fresh CRC. Values are clamped to their bit width. */
export function packFields(m: FieldMap): Uint8Array {
  const bytes = new Uint8Array(DNA_BYTES);
  for (const f of DNA_FIELDS) {
    if (f.kind === 'crc') continue;
    const raw = f.name === 'ver' ? 1 : (m[f.name] ?? 0);
    const v = Math.max(0, Math.min(fieldMax(f), Math.trunc(Number.isFinite(raw) ? raw : 0)));
    writeBits(bytes, FIELD_OFFSET.get(f.name)!, f.bits, v);
  }
  writeBits(bytes, CRC_BITS, 10, crc10(bytes, CRC_BITS));
  return bytes;
}

/** 56 bytes → field map, or null on a bad length, CRC or version. */
export function unpackFields(bytes: Uint8Array): FieldMap | null {
  if (bytes.length !== DNA_BYTES) return null;
  if (readBits(bytes, CRC_BITS, 10) !== crc10(bytes, CRC_BITS)) return null;
  const m: FieldMap = {};
  for (const f of DNA_FIELDS) m[f.name] = readBits(bytes, FIELD_OFFSET.get(f.name)!, f.bits);
  if (m.ver !== 1) return null;
  return m;
}

/** Resolves the display name of a field map (custom suffix wins when `nameMode = 1` and it validates). */
function nameFor(m: FieldMap, custom: string | null): string {
  if (m.nameMode && custom) return custom;
  return generatedName(m.title ?? 0, m.name ?? 0, m.epithet ?? 0, m.pronoun ? 1 : 0);
}

/** The custom name a DNA carries (null when generated). */
export function customNameOf(d: CreatureDNA): string | null {
  if (!d.fields.nameMode) return null;
  return validateCustomName(d.name);
}

export const dnaCodec: DnaCodec = {
  toBytes(d: CreatureDNA): Uint8Array {
    return packFields(fieldsOf(d));
  },
  fromBytes(b: Uint8Array): CreatureDNA | null {
    const m = unpackFields(b);
    if (!m) return null;
    return dnaFromFields(m, nameFor(m, null));
  },
  encode(d: CreatureDNA): string {
    const bytes = packFields(fieldsOf(d));
    let s = 'M' + toBase32(bytes, DNA_BITS);
    const custom = customNameOf(d);
    if (custom) s += '.' + base64urlEncode(custom);
    return s;
  },
  decode(code: string): CreatureDNA | null {
    if (typeof code !== 'string') return null;
    const [head, suffix] = code.split('.', 2);
    const clean = head.replace(/[\s-]+/g, '');
    if (clean.length !== DNA_CODE_LENGTH || (clean[0] !== 'M' && clean[0] !== 'm')) return null;
    const bytes = fromBase32(clean.slice(1), DNA_BITS, DNA_BYTES);
    if (!bytes || clean.length - 1 !== PAYLOAD_CHARS) return null;
    const m = unpackFields(bytes);
    if (!m) return null;
    const customRaw = suffix ? base64urlDecode(suffix.trim()) : null;
    const custom = customRaw ? validateCustomName(customRaw) : null;
    return dnaFromFields(m, nameFor(m, custom));
  },
};

/** Groups the 91-char code by 5 with hyphens (creatures §A4.2 F5 display). */
export function groupCode(code: string): string {
  const [head, suffix] = code.split('.', 2);
  const g = head.match(/.{1,5}/g)?.join('-') ?? head;
  return suffix ? `${g}.${suffix}` : g;
}

/** A `CreatureDNA` from a full field map (generated name unless a valid custom name is given). */
export function buildDNA(m: FieldMap, custom?: string | null): CreatureDNA {
  const c = custom ? validateCustomName(custom) : null;
  const mm = { ...m, nameMode: c ? 1 : 0 };
  return dnaFromFields(mm, nameFor(mm, c));
}

/** Recomputes `name` after a field edit (title/name/epithet/pronoun/nameMode). */
export function withResolvedName(d: CreatureDNA, custom?: string | null): CreatureDNA {
  const m = fieldsOf(d);
  const c = custom === undefined ? customNameOf(d) : custom ? validateCustomName(custom) : null;
  if (c) m.nameMode = 1;
  return dnaFromFields(m, nameFor(m, c));
}
